import { useFeatures } from "@/api/app-configuration.api";
import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { RadioCard } from "@/components/generic/RadioCard";
import {
  essentialsText,
  trialText,
  useTrialPeriod,
} from "@/hooks/use-trial-period";
import { planningTypes } from "@/types/campaign";
import { store } from "@/utils/jotai";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { RadioGroup } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

export const Route = createFileRoute(
  "/_dashboard/campaigns/setup/planning/type",
)({
  component: RouteComponent,
  staticData: {
    meta: {
      title: "Kampagnetype",
    },
  },
  loader: ({ context }) => ({
    hideSubmitButton: !store.get(context.newCampaignStateAtom).planning,
  }),
});

const formSchema = z.object({
  type: z
    .enum(planningTypes.map((m) => m.type))
    .nullable()
    .refine((value) => value !== null, "Vælg en type"),
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const stepper = context.useStepper();

  const { canUseTriggers } = useFeatures();
  const [_, setTrialCTAModalProps] = context.useTrialCTAModal();

  const trialPeriod = useTrialPeriod();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: stepper.context.planning?.type ?? null,
    },
  });

  const submit = useMemo(
    () =>
      handleSubmit(({ type }) => {
        if (type === null) throw Error("No type selected");

        if (trialPeriod && type !== "OnDemand") {
          setTrialCTAModalProps({
            title: trialText,
          });
          return;
        }

        stepper.setContext((prev) => {
          if (type === prev.planning?.type) return prev;

          const base = {
            deliverContacts: prev.planning?.deliverContacts ?? null,
            deliverContactsRequirePhone:
              prev.planning?.deliverContactsRequirePhone ?? null,
            deliverLiveNumber: prev.planning?.deliverLiveNumber ?? null,
          };

          switch (type) {
            case "OnDemand":
              return {
                ...prev,
                planning: {
                  ...base,
                  type: "OnDemand",
                  leadAmount: trialPeriod ? 10 : null,
                },
              };

            case "Scheduled":
              return {
                ...prev,
                planning: {
                  ...base,
                  type: "Scheduled",
                  schedules: [],
                  chosenStartDate: prev.planning?.chosenStartDate ?? null,
                  chosenEndDate: prev.planning?.chosenEndDate ?? null,
                },
              };

            case "Triggered":
              return {
                ...prev,
                planning: {
                  ...base,
                  type: "Triggered",
                  triggers: [],
                  maxLeadsPerWeek: null,
                  chosenStartDate: prev.planning?.chosenStartDate ?? null,
                  chosenEndDate: prev.planning?.chosenEndDate ?? null,
                },
              };
          }
        });

        stepper.goNext();
      }),
    [handleSubmit, stepper],
  );

  context.stepper.useStepperSubmit(
    useMemo(() => ({ submit, isDisabled: !isValid }), [submit, isValid]),
  );

  return (
    <form className="flex flex-col gap-y-10" onSubmit={submit}>
      <SegmentHeader
        className="mx-auto max-w-lg"
        title="Vælg kampagnetype"
        description="Kampagnetypen afgør, hvornår og hvor ofte du modtager leads. Du vil du altid kunne trække flere leads, hvis du vælger On-Demand eller Flow."
      />

      <Controller
        control={control}
        name="type"
        render={({ field }) => (
          <RadioGroup
            className="flex grow justify-center gap-x-6"
            value={field.value}
            onChange={(value) => {
              if (!canUseTriggers && value === "Triggered") return;
              field.onChange(value);
            }}
            aria-label="Vælg type"
            onFocus={(e) => {
              e.preventDefault();
            }}
          >
            {planningTypes.map((method) => (
              <RadioCard
                key={method.type}
                className="group flex size-full max-w-sm flex-col items-center justify-center gap-y-6 rounded-lg p-6 xl:p-12"
                value={method.type}
                onPress={() => {
                  if (trialPeriod && method.type !== "OnDemand") {
                    setTrialCTAModalProps({
                      title: trialText,
                    });
                    return;
                  }

                  if (!canUseTriggers && method.type === "Triggered") {
                    setTrialCTAModalProps({
                      title: essentialsText,
                    });
                    return;
                  }

                  field.onChange(method.type);
                  submit();
                }}
                isDisabled={
                  stepper.context.delivery?.type === "Excel" &&
                  method.type === "Triggered"
                }
                aria-label={method.name}
              >
                <div className="text-theme-gradient-to bg-theme-gradient-to/30 border-theme-gradient-to/40 group-disabled:text-theme-on-background-variant group-disabled:bg-theme-on-background-variant/30 group-disabled:border-theme-on-background-variant/40 flex size-12 items-center justify-center rounded-full border">
                  <method.icon className="size-6" />
                </div>

                <div className="flex max-w-48 flex-col gap-y-4">
                  <div className="group-disabled:text-theme-on-background/40 flex justify-center gap-x-2 text-center text-base/normal font-semibold">
                    {method.name}
                    {!canUseTriggers && method.type === "Triggered" ? (
                      <span className="text-theme-on-primary bg-theme-primary inline-flex items-center rounded-full px-2 py-1 text-[0.625rem]/2 font-bold">
                        Pro
                      </span>
                    ) : null}
                  </div>

                  <p className="text-theme-on-background-variant group-disabled:text-theme-on-background-variant/60 max-w-md text-center text-sm/normal">
                    {method.type === "Triggered" &&
                    stepper.context.delivery?.type === "Excel"
                      ? "Ikke tilgængelig ved levering til Excel"
                      : method.description}
                  </p>
                </div>
              </RadioCard>
            ))}
          </RadioGroup>
        )}
      />
    </form>
  );
}
